import { createLogger } from '@sim/logger'
// Relative on purpose: next.config.ts loads env-flags (and so this module) outside the
// app's alias resolution — see env-flags's own './env' import.
import { env, envNumber } from './env'

/**
 * `COST_MULTIPLIER` arrives as a raw string under `skipValidation` (see `envNumber`).
 * A string multiplier fails the finite check every sandbox lease runs — on dev, every
 * `run_code` died as "Boot sandbox" until the value was normalized at this boundary.
 * Outside production the multiplier is always 1.
 */
export function resolveCostMultiplier(
  raw: number | string | undefined | null,
  prod: boolean
): number {
  return prod ? envNumber(raw, 1) : 1
}

/**
 * omnyqra: USD list prices billed in euros at the ECB reference rate (Omnyqra/sim#83), see FORK.md
 *
 * Process-wide USD→EUR rate used wherever a USD list price becomes a billable
 * amount. Kept free of database code because `env-flags.ts` (loaded by
 * `next.config.ts`) depends on it: the stored rate is pushed in by
 * `lib/billing/exchange-rate/loader.ts`, which also registers the refresh hook.
 *
 * State lives on `globalThis` because Next.js bundles instrumentation and route
 * handlers separately, so a module-level variable could exist twice.
 */

const logger = createLogger('ExchangeRate')

/**
 * A rate older than this is reported. Age counts from the rate date, and over
 * Easter (or Christmas on a Thursday) the next rate lands about five days and
 * seventeen hours after the last one.
 */
export const EXCHANGE_RATE_STALE_AFTER_DAYS = 6

const ERROR_LOG_INTERVAL_MS = 10 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

export interface StoredExchangeRate {
  /** US dollars per euro, as the ECB quotes it. */
  usdPerEur: number
  /** Reference date of the rate, `YYYY-MM-DD`. */
  rateDate: string
  source: string
}

type ExchangeRateOrigin = 'stored' | 'fallback' | 'none'

export interface ExchangeRateSnapshot {
  usdPerEur: number
  rateDate: string | null
  source: string
  origin: ExchangeRateOrigin
  markupPercent: number
  /** Multiply a USD amount by this to get the billed amount in the billing currency. */
  factor: number
}

interface ExchangeRateState {
  rate: StoredExchangeRate | null
  refresh: (() => void) | null
  lastErrorLogAt: Map<string, number>
}

const STATE_KEY = Symbol.for('omnyqra.exchangeRate')

function getState(): ExchangeRateState {
  const holder = globalThis as typeof globalThis & { [STATE_KEY]?: ExchangeRateState }
  holder[STATE_KEY] ??= { rate: null, refresh: null, lastErrorLogAt: new Map() }
  return holder[STATE_KEY]
}

/** Replaces the process-wide rate. Called by the loader after reading the database. */
export function setStoredExchangeRate(rate: StoredExchangeRate | null): void {
  getState().rate = rate
}

/** Registers the loader's refresh trigger; the reader calls it on every lookup and the loader throttles. */
export function registerExchangeRateRefresh(refresh: (() => void) | null): void {
  getState().refresh = refresh
}

export function isExchangeRateStale(rateDate: string, now: Date = new Date()): boolean {
  const age = now.getTime() - Date.parse(`${rateDate}T00:00:00Z`)
  return !(age < EXCHANGE_RATE_STALE_AFTER_DAYS * DAY_MS)
}

function logErrorThrottled(key: string, message: string, meta: Record<string, unknown>): void {
  const state = getState()
  const now = Date.now()
  const last = state.lastErrorLogAt.get(key)
  if (last !== undefined && now - last < ERROR_LOG_INTERVAL_MS) return
  state.lastErrorLogAt.set(key, now)
  logger.error(message, meta)
}

/**
 * Resolves the rate to bill with: the stored ECB rate, else `USD_EUR_FALLBACK_RATE`,
 * else 1:1. Every degraded case is logged as an error (throttled) so it never goes
 * unnoticed.
 */
export function getExchangeRateSnapshot(now: Date = new Date()): ExchangeRateSnapshot {
  const state = getState()
  try {
    state.refresh?.()
  } catch (error) {
    logErrorThrottled('refresh', 'Exchange rate refresh could not be started', { error })
  }

  const markupPercent = envNumber(env.USD_EUR_MARKUP_PERCENT, 0)
  const build = (
    usdPerEur: number,
    rateDate: string | null,
    source: string,
    origin: ExchangeRateOrigin
  ): ExchangeRateSnapshot => ({
    usdPerEur,
    rateDate,
    source,
    origin,
    markupPercent,
    factor: (1 + markupPercent / 100) / usdPerEur,
  })

  if (state.rate) {
    const { usdPerEur, rateDate, source } = state.rate
    if (isExchangeRateStale(rateDate, now)) {
      logErrorThrottled('stale', 'Stored USD/EUR exchange rate is stale; billing with it anyway', {
        rateDate,
        usdPerEur,
        staleAfterDays: EXCHANGE_RATE_STALE_AFTER_DAYS,
      })
    }
    return build(usdPerEur, rateDate, source, 'stored')
  }

  const fallback = envNumber(env.USD_EUR_FALLBACK_RATE, 0)
  if (fallback > 0) {
    logErrorThrottled(
      'fallback',
      'No stored USD/EUR exchange rate; billing with USD_EUR_FALLBACK_RATE',
      {
        usdPerEur: fallback,
      }
    )
    return build(fallback, null, 'env:USD_EUR_FALLBACK_RATE', 'fallback')
  }

  logErrorThrottled(
    'none',
    'No USD/EUR exchange rate stored and USD_EUR_FALLBACK_RATE unset; billing USD amounts 1:1',
    {}
  )
  return build(1, null, 'none', 'none')
}

/** Factor that turns a USD list price into the billed amount (rate plus `USD_EUR_MARKUP_PERCENT`). */
export function getUsdToBillingFactor(): number {
  return getExchangeRateSnapshot().factor
}
